import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { botResponses } from '../../data/chatResponses';

interface ChatMessage {
  id: number;
  text: string;
  isAnimating: boolean;
  isFading: boolean;
}

const ChatButton: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageId, setMessageId] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(false);

  // Check if scrolled to bottom of ThreeSection
  useEffect(() => {
    const handleScroll = () => {
      const threeSection = document.getElementById('threejs-section');
      if (threeSection) {
        const sectionRect = threeSection.getBoundingClientRect();
        const sectionBottom = sectionRect.bottom;
        const windowHeight = window.innerHeight;
        const isAtBottom = sectionBottom <= windowHeight + 100; // 100px threshold
        setIsAtBottom(isAtBottom);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const animateText = (messageId: number, text: string) => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, text: text.slice(0, currentIndex) }
            : msg
        ));
        currentIndex++;
      } else {
        clearInterval(interval);
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, isAnimating: false }
            : msg
        ));
        
        // Start fade out after text animation completes, then remove after fade completes
        setTimeout(() => {
          setMessages(prev => prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, isFading: true }
              : msg
          ));
          
          // Remove message after fade animation completes
          setTimeout(() => {
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
          }, 1000); // 1 second fade duration
        }, 2000); // Wait 2 seconds after text animation completes
      }
    }, 50); // Speed of typing animation
  };

  const handleChatClick = () => {
    const randomText = botResponses[Math.floor(Math.random() * botResponses.length)];
    const newMessageId = messageId + 1;
    
    setMessageId(newMessageId);
    setMessages(prev => [...prev, {
      id: newMessageId,
      text: '',
      isAnimating: true,
      isFading: false
    }]);
    
    setTimeout(() => animateText(newMessageId, randomText), 100);
  };

  return (
    <div className={`${isAtBottom ? 'absolute' : 'fixed'} ${isAtBottom ? 'bottom-4 left-4' : 'bottom-4 left-4'} z-40`}>
      <button
        onClick={handleChatClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="bg-black border-2 border-green-500 rounded-lg shadow-lg shadow-green-500/20 relative transition-all duration-500 hover:scale-110 hover:shadow-green-500/40 overflow-hidden"
      >
        <img 
          src="/chat_portrait.png" 
          alt="Chat Portrait" 
          className="w-16 h-16 object-cover"
        />
        
        {/* Hover animation */}
        {isHovered && (
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 font-mono text-green-400 font-bold text-lg animate-bounce flex items-center gap-1 z-40">
            <MessageCircle className="w-4 h-4" />
            TALK
          </div>
        )}
      </button>

      {/* Chat Text Boxes */}
      {messages.map((message, index) => (
        <div 
          key={message.id}
          className={`absolute bg-black border-2 border-green-500 rounded-lg shadow-lg shadow-green-500/20 p-4 max-w-xs w-80 z-30 transition-all duration-500 ${
            message.isFading ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ 
            bottom: `${40 + ((messages.length - 1 - index) * 120)}px`, 
            left: '120px' 
          }}
        >
          <div className="font-mono text-white text-sm leading-relaxed">
            {message.text}
            {message.isAnimating && <span className="animate-pulse">|</span>}
          </div>
        </div>
      ))}

      {/* Zuko Label with Squiggly Arrow */}
      <div className="absolute bottom-12 left-20 z-30 transition-all duration-500">
        <div className="font-handwritten text-black text-sm font-bold text-center">
          This is my dog Zuko
        </div>
        {/* Simple Left Arrow */}
        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-green-500 text-4xl font-bold">
          ←
        </div>
      </div>
    </div>
  );
};

export default ChatButton; 